/**
 * Key-frame channel compilation + Catmull-Rom sampling.
 *
 * Lifted out of clips.js so the procedural gait builder (gait.js) can evaluate
 * the very same curves the runtime will, without a circular import. clips.js
 * re-exports `sampleChannel` for backwards compatibility.
 */

/** One channel: sorted times + xyz values, already in radians (or metres). */
export function compile(raw, scale, cyclic) {
  const n0 = raw.length / 4;
  const keys = [];
  for (let i = 0; i < n0; i++) keys.push([raw[i * 4], raw[i * 4 + 1] * scale, raw[i * 4 + 2] * scale, raw[i * 4 + 3] * scale]);
  keys.sort((a, b) => a[0] - b[0]);
  if (cyclic && keys.length > 1) {
    if (keys[0][0] > 1e-6) keys.unshift([0, keys[keys.length - 1][1], keys[keys.length - 1][2], keys[keys.length - 1][3]]);
    const last = keys[keys.length - 1];
    if (last[0] < 1 - 1e-6) keys.push([1, keys[0][1], keys[0][2], keys[0][3]]);
    else { last[1] = keys[0][1]; last[2] = keys[0][2]; last[3] = keys[0][3]; }
  }
  const n = keys.length;
  const t = new Float32Array(n), v = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { t[i] = keys[i][0]; v[i * 3] = keys[i][1]; v[i * 3 + 1] = keys[i][2]; v[i * 3 + 2] = keys[i][3]; }
  return { n, t, v, cyclic: !!cyclic };
}

/**
 * Catmull-Rom (finite-difference cardinal) sample of one channel.
 * Writes 3 floats into `out` at `o`.
 */
export function sampleChannel(ch, x, out, o) {
  const n = ch.n, t = ch.t, v = ch.v;
  if (n === 1) { out[o] = v[0]; out[o + 1] = v[1]; out[o + 2] = v[2]; return; }
  if (x <= t[0]) { out[o] = v[0]; out[o + 1] = v[1]; out[o + 2] = v[2]; return; }
  if (x >= t[n - 1]) { const b = (n - 1) * 3; out[o] = v[b]; out[o + 1] = v[b + 1]; out[o + 2] = v[b + 2]; return; }
  let i = 0;
  while (i < n - 2 && t[i + 1] <= x) i++;
  const t1 = t[i], t2 = t[i + 1];
  const h = t2 - t1 || 1e-6;
  const s = (x - t1) / h;
  // neighbours (wrap for cyclic tracks, clamp otherwise)
  let i0 = i - 1, i3 = i + 2;
  let t0, t3;
  if (i0 < 0) { if (ch.cyclic) { i0 = n - 2; t0 = t[i0] - 1; } else { i0 = 0; t0 = t1 - h; } } else t0 = t[i0];
  if (i3 > n - 1) { if (ch.cyclic) { i3 = 1; t3 = t[i3] + 1; } else { i3 = n - 1; t3 = t2 + h; } } else t3 = t[i3];
  const s2 = s * s, s3 = s2 * s;
  const h00 = 2 * s3 - 3 * s2 + 1, h10 = s3 - 2 * s2 + s, h01 = -2 * s3 + 3 * s2, h11 = s3 - s2;
  const b0 = i0 * 3, b1 = i * 3, b2 = (i + 1) * 3, b3 = i3 * 3;
  const k1 = h / (t2 - t0 || 1e-6), k2 = h / (t3 - t1 || 1e-6);
  for (let c = 0; c < 3; c++) {
    const p0 = v[b0 + c], p1 = v[b1 + c], p2 = v[b2 + c], p3 = v[b3 + c];
    const m1 = (p2 - p0) * k1, m2 = (p3 - p1) * k2;
    out[o + c] = h00 * p1 + h10 * m1 + h01 * p2 + h11 * m2;
  }
}
